import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import type { FocusEvent, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export const TooltipProvider = TooltipPrimitive.Provider;

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Render nothing extra when there is no content. */
  disabled?: boolean;
  className?: string;
}

/**
 * A dialog or sheet moves focus to its first control when it opens; that is
 * not the viewer asking about the control, so the tooltip stays closed unless
 * the focus is keyboard focus. Preventing the default skips Radix's handler.
 */
function ignoreProgrammaticFocus(event: FocusEvent<HTMLElement>) {
  if (!isKeyboardFocus(event.currentTarget)) event.preventDefault();
}

function isKeyboardFocus(element: HTMLElement): boolean {
  try {
    return element.matches(':focus-visible');
  } catch {
    return true;
  }
}

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  disabled,
  className,
}: TooltipProps) {
  if (disabled || content === null || content === undefined || content === '') return <>{children}</>;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild onFocus={ignoreProgrammaticFocus}>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            'z-[60] max-w-[16rem] rounded-sm glass-inverted px-[0.4375rem] py-[0.1875rem] text-xs font-medium whitespace-pre-line',
            'animate-fade-in data-[state=closed]:animate-fade-out',
            className,
          )}
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
